import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { message, Modal, Button, Alert } from '@surecompanies/core_components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { AgGridReact } from '@ag-grid-community/react'
import { toPastTenseAction } from '../../../../utils/actionLabel'
import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import PlanOverridesTabsHeader from '../../../../components/PlanOverridesTabsHeader'
import GlobalOverrideFilter from '../../../../components/GlobalOverrideFilter'
import OverrideTabLayout from '../../../../components/OverrideTabLayout'
import StatusSubTabs from '../../../../components/StatusSubTabs'
import { PLAN_OVERRIDE_TAB_KEYS } from '../../../../constants'
import { mapGlobalFilters } from '../../../../hooks/useGlobalFilterMapping'
import { PLAN_MANAGEMENT_V2 } from '../../../../../../paths'
import PlanAndRateOverrideRulesGrid from '../../components/PlanAndRateOverrideRulesGrid'
import type { SelectionMode } from '../../../../components/ThreeStateHeaderCheckbox'
import NewPlanAndRateOverrideModal from '../../components/NewPlanAndRateOverrideModal'
import PlanAndRateOverrideModal, {
  type PlanAndRateOverrideModalMode,
} from '../../components/PlanAndRateOverrideModal'
import PlanAndRateLevelHistoryContent from '../../components/PlanAndRateLevelHistoryContent'
import type {
  PlanAndRateOverrideRuleResponseDto,
  PlanAndRateOverrideRuleSortField,
  SortOrder,
} from '../../../../api/dto/plan-and-rate-override-rules.dto'
import { PlanAndRateOverrideRuleStatus } from '../../enums/plan-and-rate-override-rule.enum'
import { listPlanAndRateOverrideRules } from '../../../../api/plan-and-rate-override-rules-api'
import { listBulkImportSessions } from '../../../../api/plan-override-bulk-imports-api'
import { BulkImportStatus } from '../../../../api/dto/bulk-import-session.dto'
import { planAndRateOverridesPath } from '../../paths'
import {
  useGetPlanAndRateOverrideRules,
  PLAN_AND_RATE_OVERRIDE_RULES_QUERY_KEY,
} from './hooks/useGetPlanAndRateOverrideRules'
import { useOverrideQueryParams } from '../../../../hooks/useOverrideQueryParams'
import { useTogglePlanAndRateOverrideRuleStatus } from '../../hooks/useTogglePlanAndRateOverrideRuleStatus'
import { useDeletePlanAndRateOverrideRule } from '../../hooks/useDeletePlanAndRateOverrideRule'
import { useBulkDeletePlanAndRateOverrideRules } from '../../hooks/useBulkDeletePlanAndRateOverrideRules'
import { useBulkUpdatePlanAndRateStatusByIds } from '../../hooks/useBulkUpdatePlanAndRateStatusByIds'
import { useDeletePlanAndRateOverrideRulesByFilter } from '../../hooks/useDeletePlanAndRateOverrideRulesByFilter'
import { useUpdatePlanAndRateStatusByFilter } from '../../hooks/useUpdatePlanAndRateStatusByFilter'
import BulkImportCsvModal from '../../../../components/BulkImportCsvModal'
import { downloadPlanAndRateTemplate } from '../../utils/downloadPlanAndRateTemplate'
import BulkImportSessionsContent from '../../../../components/BulkImportSessionsContent'
import { useBulkImportPlanAndRateCsv } from '../../hooks/useBulkImportPlanAndRateCsv'
import { BULK_IMPORT_SESSIONS_QUERY_KEY } from '../../../PlanBenefitOverrides/hooks/useBulkImportCsv'
import { useBulkImportSessionPolling } from '../../../PlanBenefitOverrides/hooks/useBulkImportSessionPolling'
import { getApiErrorStatus } from '../../../../utils/apiErrors'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import '../../../PlanBenefitOverrides/PlanBenefitOverrides.scss'
import AutoApproveSwitch from '../../../../components/AutoApproveSwitch'

const HISTORY_SUBTAB_KEY = 'HISTORY' as const
const BULK_IMPORT_SUBTAB_KEY = 'BULK_IMPORT' as const
type SubTabKey =
  | PlanAndRateOverrideRuleStatus
  | typeof HISTORY_SUBTAB_KEY
  | typeof BULK_IMPORT_SUBTAB_KEY

const STATUS_TAB_ITEMS: { key: SubTabKey; label: string }[] = [
  { key: PlanAndRateOverrideRuleStatus.DRAFT, label: 'Draft' },
  { key: PlanAndRateOverrideRuleStatus.APPROVED, label: 'Approved' },
  { key: PlanAndRateOverrideRuleStatus.REJECTED, label: 'Rejected' },
  { key: HISTORY_SUBTAB_KEY, label: 'History' },
  { key: BULK_IMPORT_SUBTAB_KEY, label: 'Bulk Import' },
]

const DEFAULT_PAGE_SIZE = 20

const VALID_SUBTAB_KEYS = new Set<string>([
  PlanAndRateOverrideRuleStatus.DRAFT,
  PlanAndRateOverrideRuleStatus.APPROVED,
  PlanAndRateOverrideRuleStatus.REJECTED,
  HISTORY_SUBTAB_KEY,
  BULK_IMPORT_SUBTAB_KEY,
])

function parseInitialStatus(subtab?: string): SubTabKey {
  const raw = subtab?.toUpperCase()
  if (raw && VALID_SUBTAB_KEYS.has(raw)) return raw as SubTabKey
  return PlanAndRateOverrideRuleStatus.DRAFT
}

interface ListPlanAndRateOverrideProps {
  params?: { subtab?: string }
}

export default function ListPlanAndRateOverride({
  params,
}: ListPlanAndRateOverrideProps) {
  const queryClient = useQueryClient()
  const {
    parsed,
    updateQuery,
    navigateSubtab,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
  } = useOverrideQueryParams()

  const [activeStatus, setActiveStatus] = useState<SubTabKey>(() =>
    parseInitialStatus(params?.subtab)
  )
  const [historyTotal, setHistoryTotal] = useState(0)
  const [bulkImportTotal, setBulkImportTotal] = useState(0)
  const isHistoryTab = activeStatus === HISTORY_SUBTAB_KEY
  const isBulkImportTab = activeStatus === BULK_IMPORT_SUBTAB_KEY
  const isOverrideTab = !isHistoryTab && !isBulkImportTab
  const [total, setTotal] = useState(0)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false)
  const [overrideModal, setOverrideModal] = useState<{
    ruleId: string
    mode: PlanAndRateOverrideModalMode
  } | null>(() =>
    parsed.ruleId ? { ruleId: parsed.ruleId, mode: parsed.mode } : null
  )

  useEffect(() => {
    setOverrideModal(
      parsed.ruleId ? { ruleId: parsed.ruleId, mode: parsed.mode } : null
    )
  }, [parsed.ruleId, parsed.mode])

  const globalFilters = mapGlobalFilters({
    state: parsed.state,
    issuerId: parsed.issuerId,
    hiosPlanId: parsed.hiosPlanId,
  })
  const mappedFilter = globalFilters.plansAndRates

  const [selectionMode, setSelectionMode] = useState<SelectionMode>('none')
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([])
  const gridRef = useRef<AgGridReact<PlanAndRateOverrideRuleResponseDto>>(null)
  const programmaticSelectRef = useRef(false)

  const [pollingSessionId, setPollingSessionId] = useState<string | null>(null)
  const [isLockDismissed, setIsLockDismissed] = useState(false)
  const [bulkImportRefreshSignal, setBulkImportRefreshSignal] = useState(0)
  const [bulkImportProgressSignal, setBulkImportProgressSignal] = useState(0)
  const prevPollingStatusRef = useRef<string | null>(null)

  const { data: polledSession } = useBulkImportSessionPolling(pollingSessionId)

  const { data: latestSessions } = useQuery({
    queryKey: [BULK_IMPORT_SESSIONS_QUERY_KEY, 'plans-rates', 'mount-check'],
    queryFn: () =>
      listBulkImportSessions({
        page: 1,
        limit: 1,
        overrideType: 'plans-rates',
      }),
    staleTime: 30_000,
  })

  useEffect(() => {
    const latest = latestSessions?.data[0]
    if (
      latest &&
      latest.status === BulkImportStatus.PROCESSING &&
      !pollingSessionId
    ) {
      prevPollingStatusRef.current = BulkImportStatus.PROCESSING
      setPollingSessionId(latest.id)
    }
  }, [latestSessions, pollingSessionId])

  useEffect(() => {
    if (!polledSession) return
    const prev = prevPollingStatusRef.current
    prevPollingStatusRef.current = polledSession.status

    if (prev !== BulkImportStatus.PROCESSING) return

    if (polledSession.status === BulkImportStatus.COMPLETED) {
      const created = polledSession.createdCount ?? 0
      const updated = polledSession.updatedCount ?? 0
      const errors = polledSession.errorCount ?? 0
      let msg = `Import completed: ${created} created, ${updated} updated`
      if (errors > 0) msg += `. ${errors} error(s) — download to view.`
      message.success(msg)
      setPollingSessionId(null)
      setIsLockDismissed(true)
      prevPollingStatusRef.current = null
      setBulkImportRefreshSignal((n) => n + 1)
      queryClient.invalidateQueries({
        queryKey: [
          BULK_IMPORT_SESSIONS_QUERY_KEY,
          'plans-rates',
          'mount-check',
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [PLAN_AND_RATE_OVERRIDE_RULES_QUERY_KEY],
      })
      gridRef.current?.api?.refreshServerSide({ purge: true })
    } else if (polledSession.status === BulkImportStatus.FAILED) {
      message.error('Import failed')
      setPollingSessionId(null)
      setIsLockDismissed(true)
      prevPollingStatusRef.current = null
      setBulkImportRefreshSignal((n) => n + 1)
      queryClient.invalidateQueries({
        queryKey: [
          BULK_IMPORT_SESSIONS_QUERY_KEY,
          'plans-rates',
          'mount-check',
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [PLAN_AND_RATE_OVERRIDE_RULES_QUERY_KEY],
      })
      gridRef.current?.api?.refreshServerSide({ purge: true })
    } else if (polledSession.status === BulkImportStatus.PROCESSING) {
      setBulkImportProgressSignal((n) => n + 1)
    }
  }, [polledSession, queryClient])

  const lockSession = polledSession ?? latestSessions?.data[0] ?? null
  const hasProcessingImport =
    !isLockDismissed &&
    lockSession !== null &&
    lockSession.status === BulkImportStatus.PROCESSING

  const handleDismissLock = useCallback(() => {
    setIsLockDismissed(true)
  }, [])

  const draftQuery = useGetPlanAndRateOverrideRules(
    {
      status: PlanAndRateOverrideRuleStatus.DRAFT,
      ...mappedFilter,
      page: 1,
      limit: 1,
    },
    { enabled: activeStatus !== PlanAndRateOverrideRuleStatus.DRAFT }
  )
  const approvedQuery = useGetPlanAndRateOverrideRules(
    {
      status: PlanAndRateOverrideRuleStatus.APPROVED,
      ...mappedFilter,
      page: 1,
      limit: 1,
    },
    { enabled: activeStatus !== PlanAndRateOverrideRuleStatus.APPROVED }
  )
  const rejectedQuery = useGetPlanAndRateOverrideRules(
    {
      status: PlanAndRateOverrideRuleStatus.REJECTED,
      ...mappedFilter,
      page: 1,
      limit: 1,
    },
    { enabled: activeStatus !== PlanAndRateOverrideRuleStatus.REJECTED }
  )

  const datasource = useMemo<IServerSideDatasource>(() => {
    if (!isOverrideTab) return { getRows: () => undefined }
    return {
      getRows: async (
        params: IServerSideGetRowsParams<PlanAndRateOverrideRuleResponseDto>
      ) => {
        try {
          const { startRow, endRow, sortModel } = params.request
          const pageSize = (endRow ?? DEFAULT_PAGE_SIZE) - (startRow ?? 0)
          const page = Math.floor((startRow ?? 0) / pageSize) + 1
          const sort = sortModel?.[0]
          const sortBy = sort?.colId as
            | PlanAndRateOverrideRuleSortField
            | undefined
          const sortOrder = sort?.sort as SortOrder | undefined
          const result = await listPlanAndRateOverrideRules({
            status: activeStatus as PlanAndRateOverrideRuleStatus,
            ...mappedFilter,
            page,
            limit: pageSize,
            ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
          })
          setTotal(result.meta.total)
          params.success({
            rowData: result.data,
            rowCount: result.meta.total,
          })
        } catch {
          params.fail()
        }
      },
    }
  }, [activeStatus, mappedFilter.state, mappedFilter.hiosPlanId, isOverrideTab])

  const isMultiPage = total > parsed.pageSize

  const subTabItems = useMemo(
    () =>
      STATUS_TAB_ITEMS.map((tab) => {
        if (tab.key === PlanAndRateOverrideRuleStatus.DRAFT) {
          const count =
            activeStatus === PlanAndRateOverrideRuleStatus.DRAFT
              ? total
              : draftQuery.total
          return { ...tab, count }
        }
        if (tab.key === PlanAndRateOverrideRuleStatus.APPROVED) {
          const count =
            activeStatus === PlanAndRateOverrideRuleStatus.APPROVED
              ? total
              : approvedQuery.total
          return { ...tab, count }
        }
        if (tab.key === PlanAndRateOverrideRuleStatus.REJECTED) {
          const count =
            activeStatus === PlanAndRateOverrideRuleStatus.REJECTED
              ? total
              : rejectedQuery.total
          return { ...tab, count }
        }
        if (tab.key === BULK_IMPORT_SUBTAB_KEY) {
          return { ...tab, count: bulkImportTotal || undefined }
        }
        return tab
      }),
    [
      activeStatus,
      total,
      draftQuery.total,
      approvedQuery.total,
      rejectedQuery.total,
      bulkImportTotal,
    ]
  )

  const { mutateAsync: importCsv, isPending: isBulkImporting } =
    useBulkImportPlanAndRateCsv()

  const handleNewOverride = useCallback(() => {
    setIsCreateModalOpen(true)
  }, [])

  const handleBulkImportOpen = useCallback(() => {
    setIsBulkImportOpen(true)
  }, [])

  const handleBulkImportClose = useCallback(() => {
    setIsBulkImportOpen(false)
  }, [])

  const handleTabChange = useCallback(
    (key: SubTabKey) => {
      setActiveStatus(key)
      setTotal(0)
      setSelectionMode('none')
      setSelectedPageIds([])
      setOverrideModal(null)
      gridRef.current?.api?.deselectAll()
      navigateSubtab(planAndRateOverridesPath(key))
    },
    [navigateSubtab]
  )

  useEffect(() => {
    const nextStatus = parseInitialStatus(params?.subtab)
    setActiveStatus(nextStatus)
    setTotal(0)
    setSelectionMode('none')
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [params?.subtab])

  useEffect(() => {
    setSelectionMode('none')
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [parsed.state, parsed.issuerId, parsed.hiosPlanId])

  const handleImport = useCallback(
    async (file: File) => {
      const response = await importCsv({ file })
      message.info('Import started. Track progress in the Bulk Import tab.')
      setPollingSessionId(response.id)
      setIsLockDismissed(false)
      prevPollingStatusRef.current = BulkImportStatus.PROCESSING
      handleTabChange(BULK_IMPORT_SUBTAB_KEY)
      setBulkImportRefreshSignal((n) => n + 1)
      return response
    },
    [importCsv, handleTabChange]
  )

  const handleCreateModalClose = useCallback(() => {
    setIsCreateModalOpen(false)
  }, [])

  const handleSwitchToRuleTab = useCallback(
    (status: PlanAndRateOverrideRuleStatus) => {
      if (status !== activeStatus) {
        handleTabChange(status)
      } else {
        gridRef.current?.api?.refreshServerSide({ purge: true })
      }
    },
    [activeStatus, handleTabChange]
  )

  const handleRowView = useCallback(
    (ruleId: string) => {
      setOverrideModal({ ruleId, mode: 'view' })
      updateQuery({ ruleId, mode: 'view' })
    },
    [updateQuery]
  )

  const handleRowEdit = useCallback(
    (ruleId: string) => {
      setOverrideModal({ ruleId, mode: 'edit' })
      updateQuery({ ruleId, mode: 'edit' })
    },
    [updateQuery]
  )

  const { mutateAsync: toggleStatus } = useTogglePlanAndRateOverrideRuleStatus()
  const { mutateAsync: deleteRule } = useDeletePlanAndRateOverrideRule()
  const { mutateAsync: bulkDelete } = useBulkDeletePlanAndRateOverrideRules()
  const { mutateAsync: deleteByFilter } =
    useDeletePlanAndRateOverrideRulesByFilter()
  const { mutateAsync: bulkUpdateStatus } =
    useBulkUpdatePlanAndRateStatusByIds()
  const { mutateAsync: updateByFilter } = useUpdatePlanAndRateStatusByFilter()

  const handleRowDelete = useCallback(
    (ruleId: string) => {
      Modal.confirm({
        title: 'Delete override',
        content:
          'Are you sure you want to delete this override? This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            await deleteRule(ruleId)
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success('Override deleted.')
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('delete plan and rate override failed', err)
            message.error('Failed to delete override. Please try again.')
          }
        },
      })
    },
    [deleteRule]
  )

  const handleRowToggleStatus = useCallback(
    (ruleId: string, toStatus: PlanAndRateOverrideRuleStatus) => {
      const actionLabel =
        toStatus === PlanAndRateOverrideRuleStatus.APPROVED
          ? 'Approve'
          : toStatus === PlanAndRateOverrideRuleStatus.REJECTED
            ? 'Reject'
            : 'Re-draft'
      Modal.confirm({
        title: `${actionLabel} override`,
        content: `Are you sure you want to ${actionLabel.toLowerCase()} this override?`,
        okText: actionLabel,
        okButtonProps: {
          danger: toStatus === PlanAndRateOverrideRuleStatus.REJECTED,
        },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            await toggleStatus({ id: ruleId, status: toStatus })
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success(`Override ${toPastTenseAction(actionLabel)}.`)
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
              `${actionLabel.toLowerCase()} plan and rate override failed`,
              err
            )
            message.error(
              `Failed to ${actionLabel.toLowerCase()} override. Please try again.`
            )
          }
        },
      })
    },
    [toggleStatus]
  )

  const handleRowApprove = useCallback(
    (ruleId: string) =>
      handleRowToggleStatus(ruleId, PlanAndRateOverrideRuleStatus.APPROVED),
    [handleRowToggleStatus]
  )

  const handleRowReject = useCallback(
    (ruleId: string) =>
      handleRowToggleStatus(ruleId, PlanAndRateOverrideRuleStatus.REJECTED),
    [handleRowToggleStatus]
  )

  const handleRowRedraft = useCallback(
    (ruleId: string) =>
      handleRowToggleStatus(ruleId, PlanAndRateOverrideRuleStatus.DRAFT),
    [handleRowToggleStatus]
  )

  const handleHeaderCheckboxClicked = useCallback(() => {
    if (selectionMode === 'none') {
      programmaticSelectRef.current = true
      gridRef.current?.api?.selectAll()
      const ids: string[] = []
      gridRef.current?.api?.forEachNode((node) => {
        if (node.data?.id) ids.push(node.data.id as string)
      })
      setSelectedPageIds(ids)
      setSelectionMode('page')
    } else if (selectionMode === 'page' && isMultiPage) {
      setSelectionMode('all')
    } else {
      gridRef.current?.api?.deselectAll()
      setSelectedPageIds([])
      setSelectionMode('none')
    }
  }, [selectionMode, isMultiPage])

  const handleGridSelectionChanged = useCallback((ids: string[]) => {
    setSelectedPageIds(ids)
    setSelectionMode(ids.length === 0 ? 'none' : 'page')
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectionMode('all')
  }, [])

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api?.deselectAll()
    setSelectedPageIds([])
    setSelectionMode('none')
  }, [])

  const handleGlobalStateChange = useCallback(
    (value: string | undefined) => {
      updateQuery({ state: value ?? '', page: '1' })
      setSelectionMode('none')
      setSelectedPageIds([])
      gridRef.current?.api?.deselectAll()
    },
    [updateQuery]
  )

  const handleGlobalIssuerIdChange = useCallback(
    (value: string) => {
      updateQuery({ issuerId: value, page: '1' })
      setSelectionMode('none')
      setSelectedPageIds([])
      gridRef.current?.api?.deselectAll()
    },
    [updateQuery]
  )

  const handleGlobalHiosPlanIdChange = useCallback(
    (value: string) => {
      updateQuery({ hiosPlanId: value, page: '1' })
      setSelectionMode('none')
      setSelectedPageIds([])
      gridRef.current?.api?.deselectAll()
    },
    [updateQuery]
  )

  const handleGlobalClearAll = useCallback(() => {
    updateQuery({ state: '', issuerId: '', hiosPlanId: '', page: '1' })
    setSelectionMode('none')
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [updateQuery])

  const initialSortModel = useMemo(
    () => ({ colId: parsed.sortBy, sort: parsed.sortOrder }),
    [parsed.sortBy, parsed.sortOrder]
  )

  const handlePageChanged = useCallback(() => {
    setSelectionMode((prev) => (prev === 'all' ? 'all' : 'none'))
    setSelectedPageIds([])
    gridRef.current?.api?.deselectAll()
  }, [])

  const handleBulkDelete = useCallback(() => {
    if (selectionMode === 'none') return

    if (selectionMode === 'all') {
      if (!isOverrideTab) return
      const statusFilter = activeStatus as PlanAndRateOverrideRuleStatus
      const statusLabel = statusFilter.toLowerCase()
      const hasFilter = mappedFilter.state || mappedFilter.hiosPlanId
      Modal.confirm({
        title: hasFilter
          ? 'Delete all matching overrides'
          : 'Delete all overrides',
        content: `Delete all ${total} override(s) in the ${statusLabel} tab? This cannot be undone.`,
        okText: 'Delete All',
        okButtonProps: { danger: true },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            const result = await deleteByFilter({
              status: statusFilter,
              ...mappedFilter,
            })
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success(`${result.deletedCount} override(s) deleted.`)
            setSelectionMode('none')
            setSelectedPageIds([])
          } catch (err: unknown) {
            // eslint-disable-next-line no-console
            console.error('bulk delete all plan and rate overrides failed', err)
            if (getApiErrorStatus(err) === 422) {
              message.error(
                'Filter matches more than 500 rules. Narrow the filter before deleting.'
              )
            } else {
              message.error('Failed to delete overrides. Please try again.')
            }
          }
        },
      })
      return
    }

    if (selectedPageIds.length === 0) return
    Modal.confirm({
      title: 'Delete selected overrides',
      content: `Delete ${selectedPageIds.length} selected override(s)? This cannot be undone.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Close',
      closable: true,
      onOk: async () => {
        try {
          await bulkDelete({ ids: selectedPageIds })
          gridRef.current?.api?.refreshServerSide({ purge: true })
          message.success(`${selectedPageIds.length} override(s) deleted.`)
          setSelectionMode('none')
          setSelectedPageIds([])
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('bulk delete plan and rate overrides failed', err)
          message.error('Failed to delete overrides. Please try again.')
        }
      },
    })
  }, [
    selectionMode,
    selectedPageIds,
    isOverrideTab,
    activeStatus,
    mappedFilter,
    total,
    bulkDelete,
    deleteByFilter,
  ])

  const handleBulkStatusChange = useCallback(
    (toStatus: PlanAndRateOverrideRuleStatus, label: string) => {
      if (selectionMode === 'none') return

      if (selectionMode === 'all') {
        if (!isOverrideTab) return
        const statusFilter = activeStatus as PlanAndRateOverrideRuleStatus
        const statusLabel = statusFilter.toLowerCase()
        const hasFilter = mappedFilter.state || mappedFilter.hiosPlanId
        Modal.confirm({
          title: hasFilter
            ? `${label} all matching overrides`
            : `${label} all overrides`,
          content: `${label} all ${total} override(s) in the ${statusLabel} tab?`,
          okText: label,
          okButtonProps: {
            danger: toStatus === PlanAndRateOverrideRuleStatus.REJECTED,
          },
          cancelText: 'Close',
          closable: true,
          onOk: async () => {
            try {
              const result = await updateByFilter({
                query: {
                  status: statusFilter,
                  ...mappedFilter,
                },
                targetStatus: toStatus,
              })
              gridRef.current?.api?.refreshServerSide({ purge: true })
              message.success(
                `${result.updatedCount} override(s) ${toPastTenseAction(label)}.`
              )
              setSelectionMode('none')
              setSelectedPageIds([])
            } catch (err: unknown) {
              // eslint-disable-next-line no-console
              console.error(
                'bulk status change (filter) plan and rate overrides failed',
                err
              )
              if (getApiErrorStatus(err) === 422) {
                message.error(
                  'Filter matches more than 500 rules. Narrow the filter before updating.'
                )
              } else {
                message.error('Failed to update overrides. Please try again.')
              }
            }
          },
        })
        return
      }

      if (selectedPageIds.length === 0) return
      Modal.confirm({
        title: `${label} selected overrides`,
        content: `${label} ${selectedPageIds.length} selected override(s)?`,
        okText: label,
        okButtonProps: {
          danger: toStatus === PlanAndRateOverrideRuleStatus.REJECTED,
        },
        cancelText: 'Close',
        closable: true,
        onOk: async () => {
          try {
            await bulkUpdateStatus({ ids: selectedPageIds, status: toStatus })
            gridRef.current?.api?.refreshServerSide({ purge: true })
            message.success(
              `${selectedPageIds.length} override(s) ${toPastTenseAction(label)}.`
            )
            setSelectionMode('none')
            setSelectedPageIds([])
          } catch (err: unknown) {
            // eslint-disable-next-line no-console
            console.error(
              'bulk status change (ids) plan and rate overrides failed',
              err
            )
            if (getApiErrorStatus(err) === 404) {
              message.error('One or more selected rules could not be found.')
            } else {
              message.error('Failed to update overrides. Please try again.')
            }
          }
        },
      })
    },
    [
      selectionMode,
      selectedPageIds,
      isOverrideTab,
      activeStatus,
      mappedFilter,
      total,
      bulkUpdateStatus,
      updateByFilter,
    ]
  )

  const handleOverrideModalModeChange = useCallback(
    (mode: PlanAndRateOverrideModalMode) => {
      setOverrideModal((prev) => (prev ? { ...prev, mode } : prev))
      updateQuery({ mode })
    },
    [updateQuery]
  )

  const handleOverrideModalClose = useCallback(() => {
    setOverrideModal(null)
    updateQuery({ ruleId: '', mode: '' })
  }, [updateQuery])

  return (
    <Page
      title="Plan Override"
      horizontalRule
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Plans', path: `/${PLAN_MANAGEMENT_V2}` },
            { title: 'Plan Information', path: `/${PLAN_MANAGEMENT_V2}` },
            { title: 'Plan Override', path: planAndRateOverridesPath() },
          ]}
        />
      }
    >
      <p className="subtitle">
        Configure plan and rate overrides that modify plan data during the
        review process.
      </p>

      {hasProcessingImport && (
        <Alert
          type="info"
          showIcon
          message="A bulk import is currently processing. Editing is disabled until it completes."
          action={
            <Button size="small" onClick={handleDismissLock}>
              Dismiss
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <GlobalOverrideFilter
        state={parsed.state}
        issuerId={parsed.issuerId}
        hiosPlanId={parsed.hiosPlanId}
        onStateChange={handleGlobalStateChange}
        onIssuerIdChange={handleGlobalIssuerIdChange}
        onHiosPlanIdChange={handleGlobalHiosPlanIdChange}
        onClearAll={handleGlobalClearAll}
      />

      <PlanOverridesTabsHeader activeTab={PLAN_OVERRIDE_TAB_KEYS.PLANS_RATES} />

      <OverrideTabLayout
        recordLabel={
          isHistoryTab
            ? 'History'
            : isBulkImportTab
              ? 'Bulk Import'
              : 'Plans & Rates — Override'
        }
        recordCount={
          isHistoryTab
            ? historyTotal
            : isBulkImportTab
              ? bulkImportTotal
              : total
        }
        actionsSlot={
          !isHistoryTab ? (
            <>
              <AutoApproveSwitch settingsKey="plans-rates" />
              {isBulkImporting && (
                <span className="importingIndicator">
                  <LoadingOutlined spin />
                  Importing…
                </span>
              )}
              <Button
                icon={<UploadOutlined />}
                onClick={handleBulkImportOpen}
                disabled={isBulkImporting || hasProcessingImport}
              >
                Bulk Import
              </Button>
              {!isBulkImportTab && (
                <Button
                  type="primary"
                  onClick={handleNewOverride}
                  disabled={hasProcessingImport}
                >
                  + New override
                </Button>
              )}
            </>
          ) : undefined
        }
        subTabsSlot={
          <StatusSubTabs
            items={subTabItems}
            activeKey={activeStatus}
            onChange={handleTabChange}
          />
        }
        toolbarSlot={
          isOverrideTab ? (
            <>
              {selectionMode !== 'none' && (
                <>
                  {activeStatus === PlanAndRateOverrideRuleStatus.DRAFT && (
                    <>
                      <Button
                        type="primary"
                        onClick={() =>
                          handleBulkStatusChange(
                            PlanAndRateOverrideRuleStatus.APPROVED,
                            'Approve'
                          )
                        }
                      >
                        {selectionMode === 'all'
                          ? `Approve all (${total})`
                          : `Approve selected (${selectedPageIds.length})`}
                      </Button>
                      <Button
                        danger
                        onClick={() =>
                          handleBulkStatusChange(
                            PlanAndRateOverrideRuleStatus.REJECTED,
                            'Reject'
                          )
                        }
                      >
                        {selectionMode === 'all'
                          ? `Reject all (${total})`
                          : `Reject selected (${selectedPageIds.length})`}
                      </Button>
                    </>
                  )}
                  {activeStatus === PlanAndRateOverrideRuleStatus.APPROVED && (
                    <>
                      <Button
                        onClick={() =>
                          handleBulkStatusChange(
                            PlanAndRateOverrideRuleStatus.DRAFT,
                            'Draft'
                          )
                        }
                      >
                        {selectionMode === 'all'
                          ? `Draft all (${total})`
                          : `Draft selected (${selectedPageIds.length})`}
                      </Button>
                      <Button
                        danger
                        onClick={() =>
                          handleBulkStatusChange(
                            PlanAndRateOverrideRuleStatus.REJECTED,
                            'Reject'
                          )
                        }
                      >
                        {selectionMode === 'all'
                          ? `Reject all (${total})`
                          : `Reject selected (${selectedPageIds.length})`}
                      </Button>
                    </>
                  )}
                  {activeStatus === PlanAndRateOverrideRuleStatus.REJECTED && (
                    <Button danger onClick={handleBulkDelete}>
                      {selectionMode === 'all'
                        ? `Delete all (${total})`
                        : `Delete selected (${selectedPageIds.length})`}
                    </Button>
                  )}
                </>
              )}
            </>
          ) : undefined
        }
        contentSlot={
          isHistoryTab ? (
            <PlanAndRateLevelHistoryContent
              onTotalChange={setHistoryTotal}
              defaultPageSize={parsed.pageSize}
              onPageSizeChange={handlePageSizeChange}
              filterState={parsed.state}
              filterHiosPlanId={parsed.hiosPlanId ?? ''}
              onStateChange={(v) => updateQuery({ state: v ?? '', page: '1' })}
              onHiosPlanIdChange={(v) =>
                updateQuery({ hiosPlanId: v, page: '1' })
              }
            />
          ) : isBulkImportTab ? (
            <BulkImportSessionsContent
              overrideType="plans-rates"
              onTotalChange={setBulkImportTotal}
              refreshSignal={bulkImportRefreshSignal}
              pollingRefreshSignal={bulkImportProgressSignal}
              defaultPageSize={parsed.pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          ) : (
            <PlanAndRateOverrideRulesGrid
              ref={gridRef}
              key={activeStatus}
              datasource={datasource}
              defaultPageSize={parsed.pageSize}
              initialPage={parsed.page}
              initialSortModel={initialSortModel}
              selectionMode={selectionMode}
              selectedCount={selectedPageIds.length}
              totalCount={total}
              isMultiPage={isMultiPage}
              programmaticSelectRef={programmaticSelectRef}
              onRowView={handleRowView}
              onRowEdit={hasProcessingImport ? undefined : handleRowEdit}
              onRowApprove={hasProcessingImport ? undefined : handleRowApprove}
              onRowReject={hasProcessingImport ? undefined : handleRowReject}
              onRowRedraft={hasProcessingImport ? undefined : handleRowRedraft}
              onRowDelete={hasProcessingImport ? undefined : handleRowDelete}
              onSelectionChanged={handleGridSelectionChanged}
              onHeaderCheckboxClicked={handleHeaderCheckboxClicked}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onPageChanged={handlePageChanged}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={handleSortChange}
            />
          )
        }
      />

      {isCreateModalOpen ? (
        <NewPlanAndRateOverrideModal
          open
          onClose={handleCreateModalClose}
          onCreated={handleSwitchToRuleTab}
          onRequestEdit={handleRowEdit}
        />
      ) : null}

      {overrideModal ? (
        <PlanAndRateOverrideModal
          ruleId={overrideModal.ruleId}
          initialMode={overrideModal.mode}
          onClose={handleOverrideModalClose}
          onModeChange={handleOverrideModalModeChange}
          onSaved={hasProcessingImport ? undefined : handleSwitchToRuleTab}
          canEdit={!hasProcessingImport}
        />
      ) : null}

      <BulkImportCsvModal
        open={isBulkImportOpen}
        onClose={handleBulkImportClose}
        onImport={handleImport}
        onDownloadTemplate={downloadPlanAndRateTemplate}
      />
    </Page>
  )
}
